import FamlaServices from './FamlaServices';
import { Report } from '../../swimlane/lib/types';

export const ANALYSIS_NAME_EXISTS_ERROR = 'ANALYSIS_NAME_EXISTS';

const isDuplicateAnalysisNameMessage = (description?: string): boolean => {
    if (!description) return false;
    return /existe déjà|already exist/i.test(description);
};

const throwFromApiResponse = (response: ApiResponse, fallback: string): never => {
    const description = response.body?.description || fallback;
    if (response.status === 400 && isDuplicateAnalysisNameMessage(description)) {
        throw new Error(ANALYSIS_NAME_EXISTS_ERROR);
    }
    if (response.status === 401) {
        throw new Error('Session expirée. Veuillez vous reconnecter.');
    }
    throw new Error(description);
};

/**
 * Service dédié à la gestion des analyses company-wide
 * Centralise tous les appels API pour CompanyAnalysis
 */

export interface AnalysePayload {
    text: string;
    map_list: string[];
    file_ids?: string[];
    collection_ids?: string[];
    synthesis_only?: boolean;
}

export type AnalysisScopePayload = {
    analysis_id: string;
    map_ids: string[];
    collection_ids: string[];
    collections: Array<{
        id: string;
        name: string;
        map_ids?: string[];
        linked_process_id?: string | null;
        has_responses?: boolean;
    }>;
};

export interface ApiResponse<T = any> {
    status: number;
    body: {
        data: T;
        status_code?: number;
        response_type?: string;
        description?: string;
    };
}

export interface SaveAnalysisPayload {
    name: string;
    analysis_id: string;
    map_ids?: string[];
    collection_ids?: string[];
}

export class CompanyAnalysisService {
    /**
     * Envoie un prompt d'analyse multi-processus
     * @throws Error avec message utilisateur si échec
     */
    static async submitAnalysis(
        processId: string,
        mapId: string,
        analysisId: string,
        payload: AnalysePayload,
        accessToken: string
    ): Promise<Report | Report[] | { status: 402; data: { credits_required: number; credits_available: number; deficit: number; plan?: string } }> {
        try {
            const endpoint = `/report/answer/process/${processId}/${mapId}?analyse_id=${analysisId}`;
            const response: ApiResponse<any> = await FamlaServices(
                "api_host",
                endpoint,
                "POST",
                payload,
                accessToken
            );

            if (response.status === 200 || response.status === 201) {
                return response.body.data as Report | Report[];
            } else if (response.status === 402) {
                // Retourner les données pour le modal de crédits insuffisants
                // Le backend retourne un objet avec credits_required, credits_available, etc.
                const creditsData = response.body.data as {
                    credits_required: number;
                    credits_available: number;
                    deficit: number;
                    plan?: string;
                };
                return { status: 402, data: creditsData };
            } else {
                throw new Error(response.body.description || "Erreur lors de l'envoi du prompt");
            }
        } catch (error: any) {
            if (error.message.includes('401')) {
                throw new Error("Session expirée. Veuillez vous reconnecter.");
            } else if (error.message.includes('422')) {
                throw new Error("Données invalides. Vérifiez les documents sélectionnés.");
            } else if (error.message.includes('500')) {
                throw new Error("Erreur serveur. Veuillez réessayer dans quelques instants.");
            } else if (error.message.includes('Failed to fetch')) {
                throw new Error("Impossible de contacter le serveur. Vérifiez votre connexion.");
            }
            throw new Error(error.message || "Une erreur inattendue s'est produite");
        }
    }

    /**
     * Analyse Insights basée uniquement sur des collectes (sans cartographie).
     */
    static async submitCollectAnalysis(
        analysisId: string,
        payload: AnalysePayload,
        accessToken: string,
    ): Promise<Report | Report[] | { status: 402; data: { credits_required: number; credits_available: number; deficit: number; plan?: string } }> {
        try {
            const endpoint = `/report/answer/collect?analyse_id=${analysisId}`;
            const response: ApiResponse<any> = await FamlaServices(
                "api_host",
                endpoint,
                "POST",
                payload,
                accessToken,
            );

            if (response.status === 200 || response.status === 201) {
                return response.body.data as Report | Report[];
            } else if (response.status === 402) {
                const creditsData = response.body.data as {
                    credits_required: number;
                    credits_available: number;
                    deficit: number;
                    plan?: string;
                };
                return { status: 402, data: creditsData };
            } else {
                throw new Error(response.body.description || "Erreur lors de l'envoi du prompt");
            }
        } catch (error: any) {
            if (error.message.includes('401')) {
                throw new Error("Session expirée. Veuillez vous reconnecter.");
            } else if (error.message.includes('422')) {
                throw new Error("Sélectionnez au moins une collecte avec des réponses.");
            } else if (error.message.includes('500')) {
                throw new Error("Erreur serveur. Veuillez réessayer dans quelques instants.");
            } else if (error.message.includes('Failed to fetch')) {
                throw new Error("Impossible de contacter le serveur. Vérifiez votre connexion.");
            }
            throw new Error(error.message || "Une erreur inattendue s'est produite");
        }
    }

    /**
     * Récupère le périmètre persisté d'une analyse (cartes + collectes).
     */
    static async getAnalysisScope(
        analysisId: string,
        accessToken: string
    ): Promise<AnalysisScopePayload | null> {
        try {
            const response: ApiResponse<AnalysisScopePayload> = await FamlaServices(
                "api_host",
                `/report/analysis/${analysisId}/scope`,
                "GET",
                "",
                accessToken
            );
            if (response.status === 200 && response.body?.data) {
                return response.body.data;
            }
            return null;
        } catch {
            return null;
        }
    }

    /**
     * Récupère tous les rapports d'une analyse par son analysis_id
     * @throws Error avec message utilisateur si échec
     */
    static async getAnalysisReports(
        analysisId: string,
        accessToken: string
    ): Promise<Report[]> {
        try {
            const response: ApiResponse<Report[]> = await FamlaServices(
                "api_host",
                `/report/by/analysis/${analysisId}`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                const reports = response.body.data;
                // Trier par date de création (chronologique)
                return Array.isArray(reports)
                    ? reports.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
                    : [reports];
            } else {
                throw new Error(response.body.description || "Impossible de charger l'analyse");
            }
        } catch (error: any) {
            if (error.message.includes('404')) {
                throw new Error("Analyse introuvable.");
            } else if (error.message.includes('401')) {
                throw new Error("Session expirée. Veuillez vous reconnecter.");
            }
            throw new Error(error.message || "Erreur lors du chargement de l'analyse");
        }
    }

    /**
     * Récupère tous les fichiers uploadés d'une analyse par son analysis_id
     * @throws Error avec message utilisateur si échec
     */
    static async getAnalysisFiles(
        analysisId: string,
        accessToken: string
    ): Promise<any[]> {
        try {
            const response: ApiResponse<any[]> = await FamlaServices(
                "api_host",
                `/report/files/by/analysis/${analysisId}`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                return response.body.data || [];
            } else {
                // Ne pas throw d'erreur si pas de fichiers, juste retourner tableau vide
                return [];
            }
        } catch (error: any) {
            // Ne pas bloquer le chargement si les fichiers ne sont pas récupérables
            console.warn('Impossible de charger les fichiers de l\'analyse:', error);
            return [];
        }
    }

    /**
     * Récupère toutes les analyses sauvegardées d'une company
     * Utilise l'endpoint unifié qui retourne Analysis objects avec process_name enrichi
     * @throws Error avec message utilisateur si échec
     */
    static async getCompanyRecentAnalyses(
        companyId: string,
        accessToken: string
    ): Promise<any[]> {
        try {
            const response: ApiResponse<any[]> = await FamlaServices(
                "api_host",
                `/report/saved/analyses/${companyId}`,
                "GET",
                "",
                accessToken
            );

            if (response.status === 200) {
                // L'API retourne directement les Analysis objects avec process_name enrichi
                return response.body.data || [];
            } else {
                throw new Error(response.body.description || "Impossible de charger les analyses récentes");
            }
        } catch (error: any) {
            if (error.message.includes('401')) {
                throw new Error("Session expirée. Veuillez vous reconnecter.");
            }
            // Ne pas bloquer l'app si les analyses récentes ne chargent pas
            console.error("Erreur chargement analyses récentes:", error);
            return [];
        }
    }

    /**
     * Sauvegarde une analyse avec un nom
     * @throws Error avec message utilisateur si échec
     */
    static async saveAnalysis(
        analysisId: string,
        name: string,
        accessToken: string,
        scope?: { map_ids?: string[]; collection_ids?: string[] },
    ): Promise<void> {
        try {
            const payload: SaveAnalysisPayload = {
                name,
                analysis_id: analysisId,
                map_ids: scope?.map_ids,
                collection_ids: scope?.collection_ids,
            };
            const response: ApiResponse = await FamlaServices(
                "api_host",
                `/report/analysis/${analysisId}`,
                "PUT",
                payload,
                accessToken
            );

            if (response.status !== 200 && response.status !== 201) {
                throwFromApiResponse(response, 'Erreur lors de la sauvegarde');
            }
        } catch (error: any) {
            if (error.message === ANALYSIS_NAME_EXISTS_ERROR) {
                throw error;
            }
            throw new Error(error.message || "Erreur lors de la sauvegarde de l'analyse");
        }
    }

    /**
     * Renomme une analyse sauvegardée
     * @throws Error avec message utilisateur si échec
     */
    static async renameAnalysis(
        analysisId: string,
        name: string,
        accessToken: string,
    ): Promise<void> {
        try {
            const response: ApiResponse = await FamlaServices(
                'api_host',
                `/report/analysis/${analysisId}/name?name=${encodeURIComponent(name)}`,
                'PUT',
                '',
                accessToken,
            );

            if (response.status !== 200) {
                throwFromApiResponse(response, 'Erreur lors du renommage');
            }
        } catch (error: any) {
            if (error.message === ANALYSIS_NAME_EXISTS_ERROR) {
                throw error;
            }
            throw new Error(error.message || "Erreur lors du renommage de l'analyse");
        }
    }

    /**
     * Sauvegarde un report individuel avec un nom (workflow ProcessInterview unifié)
     * Utilisé pour sauvegarder un report spécifique avec le bouton Save
     * @throws Error avec message utilisateur si échec
     */
    static async saveReportAnalysis(
        reportId: string,
        name: string,
        accessToken: string
    ): Promise<void> {
        try {
            const response: ApiResponse = await FamlaServices(
                "api_host",
                `/report/new/save/analyse?report_id=${reportId}&name=${encodeURIComponent(name)}`,
                "POST",
                {},
                accessToken
            );

            if (response.status !== 201 && response.status !== 200) {
                throwFromApiResponse(response, 'Erreur lors de la sauvegarde');
            }
        } catch (error: any) {
            if (error.message === ANALYSIS_NAME_EXISTS_ERROR) {
                throw error;
            }
            throw new Error(error.message || "Impossible de sauvegarder l'analyse");
        }
    }

    /**
     * Supprime une analyse
     * @throws Error avec message utilisateur si échec
     */
    static async deleteAnalysis(
        analysisId: string,
        accessToken: string
    ): Promise<void> {
        try {
            const response: ApiResponse = await FamlaServices(
                "api_host",
                `/report/analysis/${analysisId}`,
                "DELETE",
                "",
                accessToken
            );

            if (response.status !== 200) {
                throw new Error(response.body.description || "Erreur lors de la suppression");
            }
        } catch (error: any) {
            if (error.message.includes('401')) {
                throw new Error("Session expirée. Veuillez vous reconnecter.");
            } else if (error.message.includes('404')) {
                throw new Error("Analyse introuvable.");
            }
            throw new Error(error.message || "Erreur lors de la suppression de l'analyse");
        }
    }

    /** Métadonnées carto légères (sans swimlane JSON) pour Insights / périmètre. */
    static async listMappingSummaries(
        companyId: string,
        accessToken: string,
        mapIds?: string[],
    ): Promise<any[]> {
        try {
            const params = new URLSearchParams();
            if (mapIds?.length) {
                for (const id of mapIds) {
                    if (id) params.append('map_ids', id);
                }
            }
            const qs = params.toString();
            const response: ApiResponse<any[]> = await FamlaServices(
                'api_host',
                `/mapping/all/mapping/summary/${companyId}${qs ? `?${qs}` : ''}`,
                'GET',
                '',
                accessToken,
            );
            if (response.status === 200) {
                return response.body.data || [];
            }
            return [];
        } catch (error) {
            console.error('Erreur chargement résumés cartographies:', error);
            return [];
        }
    }
}

